<template>
  <div class="mx-auto h-[67vh] w-full max-w-195 overflow-y-auto">
    <section class="mb-3 flex w-full flex-col gap-3">
      <template v-for="(orderLine, index) in orderLines" :key="index">
        <div
          class="border-bdr-subtle overflow-hidden rounded-md border transition-all duration-300"
          :class="isOpen(index) ? 'border-bdr-subtle' : 'border-transparent'"
        >
          <div
            class="hover:bg-bgr-tertiary flex cursor-pointer items-center gap-1.5 px-2 py-1 transition-all duration-300"
            :class="isOpen(index) ? 'bg-bgr-tertiary' : ''"
            @click="toggleAccordionItem(index)"
          >
            <Button @click.exact.stop.prevent="toggleAccordionItem(index)" icon-only :variant="ButtonVariant.Tertiary">
              <Icon
                src="CaretDown"
                class="transform transition-all duration-300"
                :class="isOpen(index) ? '-rotate-180' : ''"
              />
            </Button>
            <p class="text-body-default-m">
              {{ formatOrderlineTitleShort(orderLine) || t("consignments.new_order_line") }}
            </p>
            <Tooltip
              v-if="orderLineMissingInfo(orderLine, isCrossBorder)"
              :text="t('missing_shipment_details_warning')"
            >
              <Icon src="filled/ExclamationMark" class="bg-fgr-warning rounded-full" :size="ComponentSize.md" />
            </Tooltip>
            <Tooltip v-if="orderLine.dangerous_goods_set" :text="t('order_lines.dangerous_goods_header')">
              <Icon src="Warning" filled class="text-fgr-critical rounded-full" :size="ComponentSize.md" />
            </Tooltip>
          </div>
          <div
            class="grid overflow-hidden transition-all duration-300 ease-out"
            :class="isOpen(index) ? 'grid-rows-[1fr] py-3' : 'grid-rows-[0fr]'"
          >
            <div class="min-h-0 px-3">
              <OrderLineForm
                v-model="orderLines[index]"
                :units
                :currencies
                :is-cross-border="isCrossBorder"
                :show-dangerous-goods="true"
              />
              <Button
                class="mt-1.5 w-full"
                :variant="ButtonVariant.Destructive"
                :disabled="isDeleteDisabled"
                @click="removeOrderline(index)"
                >{{ t("consignments.remove_orderline") }}</Button
              >
            </div>
          </div>
        </div>
      </template>
    </section>
    <div class="flex w-full justify-center pb-2">
      <Button class="w-full" type="button" @click="addOrderLine">{{ t("add_order_line") }}</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IOrderline } from "~/types/orderline";

  const { t } = useI18n();

  const props = defineProps<{
    isCrossBorder?: boolean;
  }>();

  const { generateOrderline, formatOrderlineTitleShort, units, currencies, orderLineMissingInfo } = useOrderlines();
  const openOrderlineIndexes = ref<number[]>([0]);

  const { value: orderLines, validate } = useField<IOrderline[]>(() => "order_lines", undefined, {
    syncVModel: true,
  });

  watch(orderLines, () => validate(), { deep: true });

  const isDeleteDisabled = computed(() => {
    return (orderLines.value?.length ?? 0) <= 1;
  });

  const isOpen = (index: number) => {
    return openOrderlineIndexes.value.includes(index);
  };

  const toggleAccordionItem = (index: number) => {
    if (openOrderlineIndexes.value.includes(index)) {
      openOrderlineIndexes.value = openOrderlineIndexes.value.filter((i) => i !== index);
    } else {
      openOrderlineIndexes.value.push(index);
    }
  };

  const addOrderLine = () => {
    if (!orderLines.value) orderLines.value = [];
    orderLines.value.push(generateOrderline());
  };

  const removeOrderline = (index: number) => {
    orderLines.value?.splice(index, 1);
  };

  onMounted(() => {
    if (!orderLines.value) orderLines.value = [];
    if (orderLines.value.length === 0) {
      addOrderLine();
    }
  });
</script>
